import { Field, Icon, Pill, Select, TextInput } from "@/components/ui";
import { useTimezone } from "@/contexts/TimezoneContext";
import { updateMe } from "@/lib/api";
import { firebaseAuth } from "@/lib/firebase";
import { TIMEZONES } from "@/lib/timezone";
import { useState } from "react";

export interface ProfileDraft {
  name: string;
  position: string;
  dept: string;
}

// 개인 정보 설정 모달 — 이름은 Firebase displayName, 직무/부서는 PATCH /me 로 저장.
export const ProfileModal = ({
  initial,
  onClose,
  onSaved,
  onToast,
}: {
  initial: ProfileDraft;
  onClose: () => void;
  onSaved: (next: ProfileDraft) => void;
  onToast: (msg: string) => void;
}) => {
  const { timezone, setTimezone } = useTimezone();
  const [draft, setDraft] = useState<ProfileDraft>(initial);
  const [tzDraft, setTzDraft] = useState<string>(timezone);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const name = draft.name.trim();
    const position = draft.position.trim();
    const dept = draft.dept.trim();
    if (!name || !position || !dept) {
      onToast("이름 · 직무 · 부서를 모두 입력해 주세요");
      return;
    }
    setSaving(true);
    try {
      if (name !== initial.name) await firebaseAuth.updateDisplayName(name);
      if (position !== initial.position || dept !== initial.dept) {
        await updateMe({ position, dept });
      }
      // timezone 은 FE 전용(localStorage) — 네트워크 호출 없이 즉시 반영.
      if (tzDraft !== timezone) setTimezone(tzDraft);
      onSaved({ name, position, dept });
      onToast("개인 정보가 저장되었습니다");
      onClose();
    } catch (e) {
      onToast(e instanceof Error ? e.message : "저장에 실패했습니다");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 440,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "var(--bg-surface)",
          borderRadius: 12,
          boxShadow: "rgba(0,0,0,0.5) 0px 8px 24px",
          padding: 24,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: 18 }}>
          <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 17 }}>
            개인 정보 설정
          </div>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--text-dim)",
              cursor: "pointer",
              display: "flex",
            }}
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="이름" en="Name">
            <TextInput value={draft.name} onChange={(v) => setDraft((d) => ({ ...d, name: v }))} />
          </Field>
          <Field label="직무" en="Position">
            <TextInput
              value={draft.position}
              onChange={(v) => setDraft((d) => ({ ...d, position: v }))}
            />
          </Field>
          <Field label="부서" en="Department">
            <TextInput value={draft.dept} onChange={(v) => setDraft((d) => ({ ...d, dept: v }))} />
          </Field>
          <Field label="시간대" en="Time zone">
            <Select value={tzDraft} onChange={setTzDraft} options={TIMEZONES} />
          </Field>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 22 }}>
          <Pill variant="ghost" size="sm" onClick={onClose}>
            취소
          </Pill>
          <Pill variant="primary" size="sm" disabled={saving} onClick={() => void handleSave()}>
            저장
          </Pill>
        </div>
      </div>
    </div>
  );
};
